import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";

function slugify(name) {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

export default function AdminOrganizationsPage() {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/organizations");
            setOrgs(data);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load organizations"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/admin/organizations", {
                name: name.trim(),
                slug: slug.trim() || undefined,
            });
            toast.success("Organization created");
            setName("");
            setSlug("");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(extractError(err, "Could not create organization"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-orgs-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                        Platform admin
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                        Organizations
                    </h1>
                    <p className="mt-2 text-sm text-[#0E1B2C]/70 max-w-xl">
                        Provision districts, generate slugs and manage tenants.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white h-10 px-5"
                            data-testid="create-org-btn"
                        >
                            <Plus size={16} /> New organization
                        </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="create-org-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl text-[#0B2545]">
                                Create organization
                            </DialogTitle>
                            <DialogDescription>
                                Slug is used as the tenant handle. Leave blank to auto-generate.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4" data-testid="create-org-form">
                            <div className="space-y-2">
                                <Label htmlFor="org-name">Name</Label>
                                <Input
                                    id="org-name"
                                    required
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        if (!slug || slug === slugify(name)) {
                                            setSlug(slugify(e.target.value));
                                        }
                                    }}
                                    placeholder="Riverside Unified School District"
                                    data-testid="org-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="org-slug">Slug</Label>
                                <Input
                                    id="org-slug"
                                    value={slug}
                                    onChange={(e) => setSlug(slugify(e.target.value))}
                                    placeholder="riverside"
                                    data-testid="org-slug-input"
                                />
                            </div>
                            <DialogFooter>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                                    data-testid="create-org-submit"
                                >
                                    {submitting ? (
                                        <Loader2 className="animate-spin" size={16} />
                                    ) : (
                                        <Plus size={16} />
                                    )}
                                    Create
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? (
                <div className="text-[#0B2545] flex items-center gap-3" data-testid="orgs-loading">
                    <Loader2 className="animate-spin" size={18} /> Loading organizations…
                </div>
            ) : error ? (
                <div className="text-red-700" data-testid="orgs-error">{error}</div>
            ) : orgs.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center" data-testid="orgs-empty">
                    <Building2 size={28} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-3 text-[#0B2545] font-semibold">No organizations yet.</p>
                    <p className="text-sm text-[#0E1B2C]/70">Create your first tenant to get started.</p>
                </div>
            ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="orgs-list">
                    {orgs.map((o) => (
                        <li key={o.id}>
                            <Link
                                to={`/app/admin/organizations/${o.id}`}
                                className="block rounded-xl bg-white border border-[#D6E6F2] p-5 hover:border-[#8AC4FF] hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
                                data-testid={`org-card-${o.slug}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.25em] text-[#1E56A0] font-bold">
                                            {o.slug}
                                        </div>
                                        <div className="mt-1 font-serif text-xl text-[#0B2545] font-semibold">
                                            {o.name}
                                        </div>
                                    </div>
                                    <ArrowRight className="text-[#1E56A0]" size={18} />
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
